import { useEffect, useState } from 'react';
import { getWorkArrangement, listEmployees, requestWorkArrangement, type EmployeeRecord, type WorkArrangement } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { Icon } from '../../components/Icon';
import { Pending } from './shared';

const STEPS = ['Request', 'Manager review', 'HR review', 'Authorised approval', 'Effective date', 'Remote status'];

const STATUS_STEP: Record<string, number> = {
  none: -1, requested: 0, manager_review: 1, hr_review: 2, approved: 5, rejected: -1, withdrawn: -1,
};

function Workflow({ status }: { status: string }) {
  const at = STATUS_STEP[status] ?? -1;
  return (
    <ol className="org-workflow">
      {STEPS.map((s, i) => (
        <li key={s} className={i < at ? 'done' : i === at ? 'current' : ''}>
          <span className="org-workflow-dot" />
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function WorkArrangements() {
  const { can, session } = useAuth();
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [arrangements, setArrangements] = useState<Record<string, WorkArrangement>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState('');
  const [wanted, setWanted] = useState<'office' | 'remote' | 'hybrid'>('remote');
  const [category, setCategory] = useState('');
  const [effective, setEffective] = useState('');
  const [review, setReview] = useState('');
  const [hours, setHours] = useState('');
  const [equipment, setEquipment] = useState('');

  const refresh = async () => {
    const list = await listEmployees({});
    setEmployees(list.items);
    if (!target && list.items.length) setTarget(list.items[0].id);
    const pairs = await Promise.all(list.items.map(async (e) => {
      try { return [e.id, await getWorkArrangement(e.id)] as const; }
      catch { return null; }
    }));
    setArrangements(Object.fromEntries(pairs.filter(Boolean) as Array<readonly [string, WorkArrangement]>));
  };

  useEffect(() => { refresh().catch((e: Error) => setError(e.message)); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await requestWorkArrangement({
        employeeId: target, requestedArrangement: wanted,
        reasonCategory: category || null,
        proposedEffectiveFrom: effective || null,
        reviewDate: review || null,
        workingHours: hours || null,
        equipmentIssued: equipment || null,
      });
      setNotice(`Request ${res.reference} recorded. The work arrangement is unchanged until every approval step completes.`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the request.');
    } finally { setBusy(false); }
  }

  const canRequestForOthers = can('org.structure.manage');
  const requestable = canRequestForOthers ? employees : employees.filter((e) => e.id === session?.user.employeeId);

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="calendarCheck" size="xs" /><span>HR Governance</span></span>
          <h1>Work Arrangements</h1>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="banner info" style={{ marginBottom: 14 }}>{notice}</div>}

      <div className="banner info" style={{ marginBottom: 14 }}>
        A request is a record, not a change. An arrangement becomes Remote only after manager review, HR review and
        authorised approval. Personal and medical reasons are never stored here — only a category.
      </div>

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>Current arrangements</strong>
            <span className="muted" style={{ fontSize: 12 }}>{employees.length} on record</span>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th style={{ width: 150 }}>Arrangement</th>
                  <th style={{ width: 150 }}>Requested</th>
                  <th style={{ width: 160 }}>Request status</th>
                  <th style={{ width: 120 }}>Review date</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => {
                  const a = arrangements[e.id];
                  return (
                    <tr key={e.id}>
                      <td>
                        <strong>{e.first_name} {e.last_name}</strong>
                        <div className="muted" style={{ fontSize: 12 }}><Pending>{e.designation ?? undefined}</Pending></div>
                      </td>
                      <td><Pending>{a?.currentArrangement}</Pending></td>
                      <td>{a?.requestedArrangement ?? <span className="muted">—</span>}</td>
                      <td>
                        {a && a.requestStatus !== 'none'
                          ? <span className={`org-chip org-chip-${a.requestStatus}`}>{a.requestStatus.replace(/_/g, ' ')}</span>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="mono">{a?.reviewDate?.slice(0, 10) ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="org-side">
          <div className="panel">
            <div className="panel-head"><strong>Approval route</strong></div>
            <Workflow status={arrangements[target]?.requestStatus ?? 'none'} />
          </div>

          {can('work_arrangement.request') && (
            <form className="panel" onSubmit={submit}>
              <div className="panel-head"><strong>Raise a request</strong></div>
              <div className="org-form org-form-stack">
                <label>
                  <span>Employee</span>
                  <select value={target} onChange={(e) => setTarget(e.target.value)}>
                    {requestable.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                  </select>
                </label>
                <label>
                  <span>Requested arrangement</span>
                  <select value={wanted} onChange={(e) => setWanted(e.target.value as typeof wanted)}>
                    <option value="remote">Remote / WFH</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="office">Office</option>
                  </select>
                </label>
                <label>
                  <span>Reason category</span>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Not stated</option>
                    <option value="operational">Operational</option>
                    <option value="personal">Personal</option>
                    <option value="relocation">Relocation</option>
                    <option value="accommodation">Accommodation</option>
                  </select>
                </label>
                <label><span>Proposed effective date</span><input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} /></label>
                <label><span>Review date</span><input type="date" value={review} onChange={(e) => setReview(e.target.value)} /></label>
                <label><span>Working hours</span><input value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 10:00–18:30 IST" /></label>
                <label><span>Equipment issued</span><input value={equipment} onChange={(e) => setEquipment(e.target.value)} /></label>
              </div>
              <div className="org-form-actions">
                <button className="btn-primary" type="submit" disabled={busy || !target}>Record request</button>
              </div>
              <p className="muted org-footnote">
                If an accommodation is involved, keep the detail out of this form. It belongs in a restricted HR record.
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
